import { ReactNode } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import { ROUTE_TABS_HEIGHT } from "~/components/RouteTabs";

interface ScrollableTabContentProps {
  /** Content to render inside the scrollable area */
  children: ReactNode;
  /** Optional background color */
  bgcolor?: string;
  /** Optional additional sx props to merge with the container */
  sx?: SxProps<Theme>;
}

/**
 * A wrapper component that constrains content to the available viewport height
 * below the app header and RouteTabs, with vertical scrolling.
 *
 * Use this component for pages rendered inside RouteTabs to ensure the scrollbar
 * only appears in the content area and doesn't extend into the tab bar.
 *
 * @example
 * ```tsx
 * <ScrollableTabContent bgcolor="common.dimmedPageBackground">
 *   <Container maxWidth="lg">
 *     {content}
 *   </Container>
 * </ScrollableTabContent>
 * ```
 */
const ScrollableTabContent = ({ children, bgcolor, sx }: ScrollableTabContentProps) => {
  const contentHeight = `calc(100vh - ${HEADER_HEIGHT} - ${ROUTE_TABS_HEIGHT}px)`;

  return (
    <Box
      sx={{
        height: contentHeight,
        overflowY: "auto",
        bgcolor,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default ScrollableTabContent;

// Export the height calculation for pages that need custom layouts
export const getScrollableContentHeight = () =>
  `calc(100vh - ${HEADER_HEIGHT} - ${ROUTE_TABS_HEIGHT}px)`;
